/*
 * Copyright © 2026 Medaica, Inc
 *
 * All rights reserved.
 *
 * This code is confidential and proprietary information belonging to Medaica, Inc.
 * Unauthorized copying, distribution, or use of this code, in whole or in part,
 * is strictly prohibited, and may constitute a violation of intellectual property rights.
 *
 * If you have received this code in error, please notify the owner immediately
 * at support@medaica.com and delete this file from your system.
 */

import React, { ReactElement, useCallback, useEffect, useMemo, useRef, useState } from "react"
import { observer } from "mobx-react-lite"
import { Button } from "@mui/material"
import { addDays } from "date-fns"
import { useSnackbar } from "notistack"
import Loading from "@medaica/common/components/loading"
import { isMedaicaApiError, MedaicaApiErrorType } from "@medaica/common/services/medaica-api-service"
import { logError } from "@medaica/common/services/util"
import { BookingSlot } from "@medaica/common/types"
import { useBookingStores } from "../booking-context"
import WizardStepLayout from "../components/wizard-step-layout"
import SlotGrid from "../components/slot-grid"
import generateRequestId from "../api/request-id"
import bookingCopy from "../bookingCopy"
import { bookingColors, footerButtonSx } from "../booking-theme"
import { InformationCircleIcon } from "@heroicons/react/solid"
import { clampPage, pageWindow, pagingBase } from "../slot-paging"
import { US_TIME_ZONES, usTimeZoneLabel } from "../timezones"

const copy = bookingCopy.time

const errorType = (error: unknown): MedaicaApiErrorType | null =>
  isMedaicaApiError(error) ? (error as { type: MedaicaApiErrorType }).type : null

// Date included so a selection stays unambiguous across the multi-week booking horizon.
const formatSelected = (slot: BookingSlot, timeZone: string): string =>
  new Intl.DateTimeFormat("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
    timeZone,
  }).format(slot.startUtc)

const formatDay = (date: Date, timeZone: string): string =>
  new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric", timeZone }).format(date)

const Time = observer((): ReactElement => {
  const { sessionStore, uiStore, wizardStore } = useBookingStores()
  const { enqueueSnackbar } = useSnackbar()
  const config = sessionStore.config
  const [pageIndex, setPageIndex] = useState(0)
  const [slots, setSlots] = useState<BookingSlot[]>([])
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState(false)
  const [booking, setBooking] = useState(false)

  // Idempotency key for the CURRENT target slot — reused across retries so a
  // timeout-then-retry can't double-book (03 §5), but re-minted when the target
  // slot changes: the backend replays the buyer's existing appointment for a
  // reused request_id (no payload check), so one key spanning two different
  // slots could return slot A's booking from slot B's confirm.
  const requestIdRef = useRef<string>("")
  if (!requestIdRef.current) {
    requestIdRef.current = generateRequestId()
  }
  // The mount instant — a stable "now" so week windows don't drift as the session ages.
  const nowRef = useRef<Date>(new Date())
  // Pager anchor = day start in the DISPLAY zone, so page windows tile exactly the days the strip
  // renders (see pagingBase) — re-anchored when the patient changes the zone. Plus a per-page
  // cache (so prev/next is instant and doesn't re-hit the API for a week already seen).
  const base = useMemo(() => pagingBase(nowRef.current, wizardStore.timeZone), [wizardStore.timeZone])
  const cacheRef = useRef<Map<number, BookingSlot[]>>(new Map())

  // A zone change moves every page window (new anchor), so cached pages keyed by index no longer
  // match their windows — drop them BEFORE the load effect below refetches the visible page.
  useEffect(() => {
    cacheRef.current.clear()
  }, [base])

  // Skip empty LEADING weeks once, on entry: with a multi-day lead the first week is often
  // legitimately empty, and landing the patient on "No times available" is a dead end. The skip
  // stops at the first week with slots (or the last page, if every week is empty) and never fires
  // again — manual paging, picking a slot, or changing the time zone all end it, and "Earlier
  // week" stays enabled so the landed-on page hides nothing.
  const autoSkipRef = useRef(true)

  // True = we advanced a page instead of showing this (empty) one; the caller keeps the spinner up.
  const maybeAutoSkip = useCallback(
    (index: number, result: BookingSlot[]): boolean => {
      if (!autoSkipRef.current) {
        return false
      }
      if (!config) {
        // No pager bound yet (config preloads at verify, so this is brief) — show the page and
        // leave the skip armed; the config effect re-runs loadPage, which lands back here.
        return false
      }
      if (result.length === 0 && index < config.horizonWeeks - 1) {
        setPageIndex(index + 1)
        return true
      }
      autoSkipRef.current = false
      return false
    },
    [config]
  )

  const loadPage = useCallback(
    async (index: number, force = false): Promise<void> => {
      if (!force) {
        const cached = cacheRef.current.get(index)
        if (cached) {
          if (maybeAutoSkip(index, cached)) {
            return
          }
          setSlots(cached)
          setLoading(false)
          setLoadError(false)
          return
        }
      }
      setLoading(true)
      setLoadError(false)
      try {
        const { from, to } = pageWindow(base, index)
        const result = await sessionStore.runSessionCall(
          () => sessionStore.api.getAvailableSlots(from, to, wizardStore.timeZone),
          "time"
        )
        if (result) {
          cacheRef.current.set(index, result)
          if (maybeAutoSkip(index, result)) {
            return
          }
          setSlots(result)
        }
      } catch (error) {
        logError(error)
        setLoadError(true)
      } finally {
        setLoading(false)
      }
    },
    [sessionStore, wizardStore.timeZone, maybeAutoSkip, base]
  )

  useEffect(() => {
    void loadPage(pageIndex)
  }, [loadPage, pageIndex])

  // Config is preloaded at verify/init; re-fetch defensively if we arrived without it. The
  // render waits on it (single source of truth — no local fallback constant).
  useEffect(() => {
    if (!config) {
      void sessionStore.loadConfig()
    }
  }, [config, sessionStore])

  // Selection intentionally survives paging — the Confirm label shows the picked slot's
  // full date/time, so the buyer can browse other weeks and still confirm their choice.
  const goPage = (delta: number): void => {
    if (!config) {
      return
    }
    autoSkipRef.current = false // manual paging — the entry skip must never move the user again
    setPageIndex((index) => clampPage(index + delta, config.horizonWeeks))
  }

  const handleSelect = (slot: BookingSlot): void => {
    autoSkipRef.current = false
    if (wizardStore.selectedSlot?.startUtc.getTime() !== slot.startUtc.getTime()) {
      requestIdRef.current = generateRequestId() // new target slot -> new idempotency key
    }
    wizardStore.setSelectedSlot(slot)
  }

  const confirm = async (): Promise<void> => {
    if (!wizardStore.selectedSlot || booking) {
      return
    }
    if (!wizardStore.patient) {
      // Can't happen in the linear flow, but never book without a patient — route back
      // to collect it rather than throwing a generic error from toBookingRequest.
      uiStore.goTo("patient")
      return
    }
    setBooking(true)
    try {
      const request = wizardStore.toBookingRequest(requestIdRef.current)
      const result = await sessionStore.runSessionCall(() => sessionStore.api.bookAppointment(request), "time")
      if (result) {
        wizardStore.setBookingResult(result)
        uiStore.goTo("done")
      }
    } catch (error) {
      const type = errorType(error)
      if (type === MedaicaApiErrorType.SLOT_TAKEN) {
        enqueueSnackbar(copy.slotTaken, { variant: "warning" })
        wizardStore.setSelectedSlot(null)
        // Capacity shifted server-side, and the taken slot may sit in ANOTHER week's
        // cache (selection survives paging) — drop every cached week, then refetch.
        cacheRef.current.clear()
        await loadPage(pageIndex, true)
      } else if (type === MedaicaApiErrorType.NO_CREDITS) {
        enqueueSnackbar(copy.noCredits, { variant: "warning" })
        uiStore.goTo("account")
        void sessionStore.loadAccount()
      } else {
        logError(error)
        enqueueSnackbar(copy.genericError, { variant: "error" })
      }
    } finally {
      setBooking(false)
    }
  }

  const handleTimeZoneChange = (event: React.ChangeEvent<HTMLSelectElement>): void => {
    // Changing the zone re-anchors the pager (see `base`), which also clears the page cache and
    // refetches the visible week in the new zone's day windows.
    autoSkipRef.current = false
    wizardStore.setTimeZone(event.target.value)
    wizardStore.setSelectedSlot(null)
  }

  const patient = wizardStore.patient
  const patientName = patient ? `${patient.firstName} ${patient.lastName}`.trim() : ""

  // Wait for the booking config — it drives the pager bound and the copy, and there's no local
  // fallback constant (the API is the source of truth). Preloaded at verify/init, so this is brief.
  if (!config) {
    return (
      <WizardStepLayout current="time" headerRight={bookingCopy.bookingFor(patientName)}>
        <Loading />
      </WizardStepLayout>
    )
  }

  const confirmLabel = wizardStore.selectedSlot
    ? copy.confirm(formatSelected(wizardStore.selectedSlot, wizardStore.timeZone))
    : copy.confirmDisabled
  const examDurationText = copy.examDuration(config.examMinutes, config.horizonWeeks, config.minLeadHours)

  const { from: pageFrom, to: pageTo } = pageWindow(base, pageIndex)
  const rangeLabel = `${formatDay(pageFrom, wizardStore.timeZone)} – ${formatDay(
    addDays(pageTo, -1),
    wizardStore.timeZone
  )}`

  let slotsBody: ReactElement
  // Keep the spinner up between auto-skip hops too: loadPage's `finally` clears `loading` for the
  // frame where the page advance is queued, which would otherwise flash "No times available".
  if (loading || (autoSkipRef.current && !loadError)) {
    slotsBody = <Loading />
  } else if (loadError) {
    slotsBody = (
      <div className="text-center py-6">
        <p className="text-sm text-gray-600">{copy.loadError}</p>
        <Button variant="outlined" sx={{ mt: 2 }} onClick={() => void loadPage(pageIndex, true)}>
          {copy.retry}
        </Button>
      </div>
    )
  } else {
    slotsBody = (
      <SlotGrid
        slots={slots}
        weekStart={pageFrom}
        timeZone={wizardStore.timeZone}
        selectedSlot={wizardStore.selectedSlot}
        onSelect={handleSelect}
      />
    )
  }

  return (
    <WizardStepLayout current="time" headerRight={bookingCopy.bookingFor(patientName)}>
      <h1 className="text-2xl font-bold" style={{ color: bookingColors.primary }}>
        {copy.title}
      </h1>
      <p className="mt-1 mb-4 text-gray-600">{examDurationText}</p>

      {/* Timezone row: info alert (left) + change dropdown (right). Same select semantics as
          before — only the presentation moved out of the heading. Colors inline (indigo tint). */}
      <div className="mb-4 grid grid-cols-1 items-stretch gap-4 sm:grid-cols-2">
        <div
          className="flex items-start gap-3 rounded-lg border px-4 py-3"
          style={{ backgroundColor: "#F0F0FB", borderColor: "#DDDDF2" }}
        >
          <InformationCircleIcon
            className="mt-0.5 h-5 w-5 flex-shrink-0"
            style={{ color: bookingColors.primary }}
            aria-hidden="true"
          />
          <div>
            <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">{copy.timesShownIn}</div>
            <div className="font-semibold text-gray-900">{usTimeZoneLabel(wizardStore.timeZone)}</div>
            <div className="mt-0.5 text-xs text-gray-500">{copy.autoDetectedNote}</div>
          </div>
        </div>
        <div>
          <label htmlFor="booking-time-zone" className="block text-sm font-medium text-gray-700">
            {copy.changeTimeZone}
          </label>
          <select
            id="booking-time-zone"
            value={wizardStore.timeZone}
            onChange={handleTimeZoneChange}
            className="input mt-1 block w-full rounded-lg py-3"
          >
            {US_TIME_ZONES.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="flex items-center justify-between mb-4">
          {/* Week pager as inline links INSIDE the card — visually distinct from the flow's Back
              button below (stakeholder feedback). booking also freezes paging: confirm()'s
              SLOT_TAKEN refetch targets the week visible when Confirm was clicked, so pageIndex
              must not move under it. */}
          <button
            type="button"
            disabled={pageIndex === 0 || loading || booking}
            onClick={() => goPage(-1)}
            className="text-sm font-medium text-primary disabled:cursor-not-allowed disabled:text-gray-300"
          >
            {copy.earlier}
          </button>
          <span className="text-sm font-medium text-gray-700">{rangeLabel}</span>
          <button
            type="button"
            disabled={pageIndex >= config.horizonWeeks - 1 || loading || booking}
            onClick={() => goPage(1)}
            className="text-sm font-medium text-primary disabled:cursor-not-allowed disabled:text-gray-300"
          >
            {copy.later}
          </button>
        </div>
        {slotsBody}
      </div>

      {/* Stacked full-width below `sm`: the dynamic Confirm label ("Confirm Tue, Sep 8, 4:30 AM")
          plus Back cannot share a 360px row. Plain flex-col (not -reverse) keeps the visual order
          equal to the DOM/tab order — Back on top, Confirm at the bottom in the thumb zone. */}
      <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <Button
          variant="outlined"
          color="inherit"
          sx={{ ...footerButtonSx, width: { xs: "100%", sm: "auto" } }}
          disabled={booking}
          onClick={() => uiStore.goTo("health")}
        >
          {copy.back}
        </Button>
        <Button
          variant="contained"
          sx={{ ...footerButtonSx, width: { xs: "100%", sm: "auto" } }}
          disabled={!wizardStore.selectedSlot || booking}
          onClick={() => void confirm()}
        >
          {confirmLabel}
        </Button>
      </div>
    </WizardStepLayout>
  )
})

export default Time
